/**
 * React Query hooks for agent templates
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentApi } from '../services/agentApi';
import {
  AgentTemplateCreate,
  AgentTemplateUpdate,
  ListTemplatesParams,
  SaveAsTemplateRequest,
} from '../types';

export const AGENT_TEMPLATES_QUERY_KEY = 'agent-templates';
export const AGENT_TEMPLATE_GALLERY_QUERY_KEY = 'agent-template-gallery';
export const AGENT_TEMPLATE_CATEGORIES_QUERY_KEY = 'agent-template-categories';

/**
 * Fetch list of templates with optional filters
 */
export function useAgentTemplates(params?: ListTemplatesParams) {
  return useQuery({
    queryKey: [AGENT_TEMPLATES_QUERY_KEY, params],
    queryFn: () => agentApi.listTemplates(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch template gallery with sections (system, company)
 */
export function useAgentTemplateGallery() {
  return useQuery({
    queryKey: [AGENT_TEMPLATE_GALLERY_QUERY_KEY],
    queryFn: () => agentApi.getTemplateGallery(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch template categories
 */
export function useAgentTemplateCategories() {
  return useQuery({
    queryKey: [AGENT_TEMPLATE_CATEGORIES_QUERY_KEY],
    queryFn: () => agentApi.getTemplateCategories(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch single template by ID
 */
export function useAgentTemplate(id: string) {
  return useQuery({
    queryKey: [AGENT_TEMPLATES_QUERY_KEY, id],
    queryFn: () => agentApi.getTemplate(id),
    enabled: !!id,
  });
}

/**
 * Save an agent as a template
 */
export function useSaveAsTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, data }: { agentId: string; data: SaveAsTemplateRequest }) =>
      agentApi.saveAsTemplate(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATE_GALLERY_QUERY_KEY] });
    },
  });
}

/**
 * Create a new tenant template (admin only)
 */
export function useCreateAgentTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AgentTemplateCreate) => agentApi.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATE_GALLERY_QUERY_KEY] });
    },
  });
}

/**
 * Update a tenant template (admin only)
 */
export function useUpdateAgentTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AgentTemplateUpdate }) =>
      agentApi.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATE_GALLERY_QUERY_KEY] });
    },
  });
}

/**
 * Delete a tenant template (admin only)
 */
export function useDeleteAgentTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentApi.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATE_GALLERY_QUERY_KEY] });
    },
  });
}

/**
 * Suppress a system template (admin only)
 */
export function useSuppressTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentApi.suppressTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATE_GALLERY_QUERY_KEY] });
    },
  });
}

/**
 * Unsuppress a system template (admin only)
 */
export function useUnsuppressTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentApi.unsuppressTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [AGENT_TEMPLATE_GALLERY_QUERY_KEY] });
    },
  });
}
